/**
 * FikoPointCloud - Point Cloud Yönetimi
 *
 * 3D nokta bulutu oluşturma, filtreleme ve işleme.
 */

import * as THREE from 'three';

/** Point cloud veri yapısı */
export interface PointCloudData {
    positions: Float32Array;
    colors: Float32Array;
    normals: Float32Array | null;
    pointCount: number;
}

/** Voxel grid ayarları */
export interface VoxelGridSettings {
    voxelSize: number;  // mm
    minPoints: number;  // Voxel başına minimum nokta
}

/**
 * Point Cloud sınıfı
 */
export class FikoPointCloud {
    private positions: number[] = [];
    private colors: number[] = [];
    private normals: number[] = [];

    constructor() {}

    /**
     * Nokta ekle
     */
    addPoint(position: THREE.Vector3, color: THREE.Color, normal?: THREE.Vector3): void {
        this.positions.push(position.x, position.y, position.z);
        this.colors.push(color.r, color.g, color.b);

        if (normal) {
            this.normals.push(normal.x, normal.y, normal.z);
        }
    }

    /**
     * Çoklu nokta ekle
     */
    addPoints(positions: THREE.Vector3[], colors: THREE.Color[], normals?: THREE.Vector3[]): void {
        for (let i = 0; i < positions.length; i++) {
            this.addPoint(
                positions[i],
                colors[i] || new THREE.Color(1, 1, 1),
                normals?.[i]
            );
        }
    }

    /**
     * Nokta sayısını al
     */
    getPointCount(): number {
        return this.positions.length / 3;
    }

    /**
     * Veriyi al
     */
    getData(): PointCloudData | null {
        const count = this.getPointCount();
        if (count === 0) return null;

        return {
            positions: new Float32Array(this.positions),
            colors: new Float32Array(this.colors),
            normals: this.normals.length > 0 ? new Float32Array(this.normals) : null,
            pointCount: count
        };
    }

    /**
     * THREE.js BufferGeometry oluştur
     */
    createGeometry(): THREE.BufferGeometry {
        const geometry = new THREE.BufferGeometry();

        if (this.positions.length > 0) {
            geometry.setAttribute('position', new THREE.Float32BufferAttribute(this.positions, 3));
        }

        if (this.colors.length > 0) {
            geometry.setAttribute('color', new THREE.Float32BufferAttribute(this.colors, 3));
        }

        if (this.normals.length > 0) {
            geometry.setAttribute('normal', new THREE.Float32BufferAttribute(this.normals, 3));
        }

        geometry.computeBoundingBox();
        geometry.computeBoundingSphere();

        return geometry;
    }

    /**
     * Voxel Grid Filtresi - Nokta yoğunluğunu azalt
     */
    voxelGridFilter(voxelSize: number = 2): void {
        const count = this.getPointCount();
        if (count === 0) return;

        const voxelMap = new Map<string, {
            sumX: number; sumY: number; sumZ: number;
            sumR: number; sumG: number; sumB: number;
            count: number;
        }>();

        // Noktaları voxel'lere grupla
        for (let i = 0; i < count; i++) {
            const idx = i * 3;

            const x = this.positions[idx];
            const y = this.positions[idx + 1];
            const z = this.positions[idx + 2];

            // Voxel indeksi
            const vx = Math.floor(x / voxelSize);
            const vy = Math.floor(y / voxelSize);
            const vz = Math.floor(z / voxelSize);
            const key = `${vx}_${vy}_${vz}`;

            if (!voxelMap.has(key)) {
                voxelMap.set(key, {
                    sumX: 0, sumY: 0, sumZ: 0,
                    sumR: 0, sumG: 0, sumB: 0,
                    count: 0
                });
            }

            const voxel = voxelMap.get(key)!;
            voxel.sumX += x;
            voxel.sumY += y;
            voxel.sumZ += z;
            voxel.sumR += this.colors[idx];
            voxel.sumG += this.colors[idx + 1];
            voxel.sumB += this.colors[idx + 2];
            voxel.count++;
        }

        // Yeni point cloud oluştur (her voxel için ortalama nokta)
        const newPositions: number[] = [];
        const newColors: number[] = [];

        voxelMap.forEach((voxel) => {
            newPositions.push(
                voxel.sumX / voxel.count,
                voxel.sumY / voxel.count,
                voxel.sumZ / voxel.count
            );
            newColors.push(
                voxel.sumR / voxel.count,
                voxel.sumG / voxel.count,
                voxel.sumB / voxel.count
            );
        });

        this.positions = newPositions;
        this.colors = newColors;
        this.normals = []; // Normal'lar yeniden hesaplanmalı

        console.log(`[PointCloud] Voxel filter: ${count} -> ${this.getPointCount()} nokta`);
    }

    /**
     * Statistical Outlier Removal - Aykırı noktaları kaldır
     */
    removeOutliers(k: number = 20, stdRatio: number = 2.0): void {
        const count = this.getPointCount();
        if (count < k) return;

        // Her nokta için k en yakın komşunun ortalama mesafesini hesapla
        const meanDistances: number[] = [];

        for (let i = 0; i < count; i++) {
            const idx = i * 3;
            const px = this.positions[idx];
            const py = this.positions[idx + 1];
            const pz = this.positions[idx + 2];

            // Tüm mesafeleri hesapla
            const distances: number[] = [];
            for (let j = 0; j < count; j++) {
                if (i === j) continue;

                const jdx = j * 3;
                const dx = this.positions[jdx] - px;
                const dy = this.positions[jdx + 1] - py;
                const dz = this.positions[jdx + 2] - pz;
                distances.push(Math.sqrt(dx * dx + dy * dy + dz * dz));
            }

            // K en yakını al ve ortalama
            distances.sort((a, b) => a - b);
            const kNearest = distances.slice(0, k);
            const meanDist = kNearest.reduce((a, b) => a + b, 0) / k;
            meanDistances.push(meanDist);
        }

        // Global ortalama ve standart sapma
        const globalMean = meanDistances.reduce((a, b) => a + b, 0) / count;
        const variance = meanDistances.reduce((sum, d) => sum + (d - globalMean) ** 2, 0) / count;
        const stdDev = Math.sqrt(variance);

        // Eşik
        const threshold = globalMean + stdRatio * stdDev;

        // Filtreleme
        const newPositions: number[] = [];
        const newColors: number[] = [];

        for (let i = 0; i < count; i++) {
            if (meanDistances[i] <= threshold) {
                const idx = i * 3;
                newPositions.push(
                    this.positions[idx],
                    this.positions[idx + 1],
                    this.positions[idx + 2]
                );
                newColors.push(
                    this.colors[idx],
                    this.colors[idx + 1],
                    this.colors[idx + 2]
                );
            }
        }

        this.positions = newPositions;
        this.colors = newColors;
        this.normals = [];

        console.log(`[PointCloud] Outlier removal: ${count} -> ${this.getPointCount()} nokta`);
    }

    /**
     * Bounding box hesapla
     */
    computeBoundingBox(): THREE.Box3 {
        const box = new THREE.Box3();
        const count = this.getPointCount();

        for (let i = 0; i < count; i++) {
            const idx = i * 3;
            box.expandByPoint(new THREE.Vector3(
                this.positions[idx],
                this.positions[idx + 1],
                this.positions[idx + 2]
            ));
        }

        return box;
    }

    /**
     * Temizle
     */
    clear(): void {
        this.positions = [];
        this.colors = [];
        this.normals = [];
    }

    /**
     * Başka bir point cloud ile birleştir
     */
    merge(other: FikoPointCloud): void {
        const otherData = other.getData();
        if (!otherData) return;

        for (let i = 0; i < otherData.pointCount; i++) {
            const idx = i * 3;
            this.positions.push(
                otherData.positions[idx],
                otherData.positions[idx + 1],
                otherData.positions[idx + 2]
            );
            this.colors.push(
                otherData.colors[idx],
                otherData.colors[idx + 1],
                otherData.colors[idx + 2]
            );
        }
    }
}
